import React from 'react';
// @ts-ignore - TypeScript casing issue
import { useRecoilValue } from 'recoil';
// @ts-ignore - TypeScript casing issue
import { speakingAudioRecordingsState } from '../../../../../recoil/atoms';
import RecapSection from '../RecapSection';
import ReportSoalButton from '../../../LanguageLearning/ReportSoalButton';
import { ReviewQuestionHeader } from '../shared/ReviewQuestionHeader';
import QuestionAudioPlayer from '../../../shared/QuestionAudioPlayer';

const SpeakingReview = ({ question, userAnswer, isCorrect, feedback, explanation, recap = '', annotations = [], reportProps = null }) => {
  // changed by Cursor: read audio from Recoil instead of localStorage (reliable, no quota issues)
  const audioRecordings = useRecoilValue(speakingAudioRecordingsState);
  console.log('🔍 SpeakingReview - Raw Props:', {
    question,
    userAnswer,
    userAnswerType: typeof userAnswer,
    isArray: Array.isArray(userAnswer),
    isObject: typeof userAnswer === 'object' && !Array.isArray(userAnswer),
    feedback,
    explanation,
    isCorrect,
  });
  
  // changed by Cursor: separate text and question
  const text = question.text || question['text-soal'] || '';
  const questionText = question.question || question.prompt || '';
  const prompt = text || questionText || ''; // Keep for backward compatibility
  const questionNumber = question.number || question['question-number'] || 1;
  
  // Extract all possible question IDs (from multiple sources)
  // This ensures we can match audio saved during practice
  const possibleQuestionIds = [
    question.originalQuestion?.['soal-id'],
    question.originalQuestion?.id,
    question.originalQuestion?.soalId,
    question['soal-id'],
    question.soalId,
    question.id,
  ].filter(Boolean); // Remove null/undefined
  
  // Primary questionId for logging (use first available)
  const questionId = possibleQuestionIds[0] || null;
  
  // Extract audio URL from userAnswer
  let audioUrl = (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.audioUrl) 
    ? userAnswer.audioUrl 
    : null;
  
  // changed by Cursor: fallback to Recoil state (replaces localStorage - reliable, no quota issues)
  if (!audioUrl) {
    const possibleIdsAsString = possibleQuestionIds.map(String);
    
    // Strategy 1: Match by soal-id
    for (const [, recording] of Object.entries(audioRecordings)) {
      if (recording?.soalId && possibleIdsAsString.includes(String(recording.soalId))) {
        audioUrl = recording.audioUrl;
        console.log('✅ SpeakingReview - Found audio in Recoil by soal-id:', recording.soalId);
        break;
      }
    }
    
    // Strategy 2: Match by index (questionNumber - 1)
    if (!audioUrl) {
      const taskIndex = (questionNumber - 1).toString();
      if (audioRecordings[taskIndex]?.audioUrl) {
        audioUrl = audioRecordings[taskIndex].audioUrl;
        console.log('✅ SpeakingReview - Found audio in Recoil by index:', taskIndex);
      }
    }
    
    if (!audioUrl) {
      console.warn('⚠️ SpeakingReview - No audio recording found:', { questionNumber, possibleQuestionIds });
    }
  } else {
    console.log('✅ SpeakingReview - Using audioUrl from props');
  }
  
  // Extract text answer if available
  const textAnswer = (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.text)
    ? userAnswer.text
    : (typeof userAnswer === 'object' && !Array.isArray(userAnswer) && userAnswer?.userAnswer
      ? userAnswer.userAnswer
      : null);
  
  // Get feedback from multiple sources
  const displayFeedback = feedback || question.feedback || question.feedbacks || explanation;

  return (
    <div className="relative flex flex-col gap-0 bg-[var(--card-bg)] rounded-xl p-6 mb-6 max-xl:px-0 max-xl:py-2" style={{ pointerEvents: 'auto' }}>
      <ReviewQuestionHeader
        questionType="Speaking"
        currentQuestionNumber={reportProps?.currentQuestionNumber}
        totalQuestions={reportProps?.totalQuestions}
        score={reportProps?.score}
        reportSlot={reportProps ? (
          <ReportSoalButton
            type={reportProps.reportType}
            prosetId={reportProps.prosetId}
            soalId={reportProps.soalId}
            allowReReport={true}
            persistReportedState={true}
            reportText="Report this question"
          />
        ) : null}
      />

      <div className="relative flex flex-col gap-0" style={{ pointerEvents: 'auto' }}>
        {/* Instruction text - blue box style */}
        {text && (
          <div className="mb-3 p-4 rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-500/10">
            <div
              className="text-[15px] leading-[1.7] text-[var(--text-primary)] [&_a]:text-blue-600 dark:[&_a]:text-blue-400"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
        )}
        
        {questionText && (
          <div className="mb-5">
            <p className="font-semibold mb-3 text-lg text-[var(--text-primary)]">Question:</p>
            <p
              className="m-0 text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: questionText }}
            />
          </div>
        )}
        
        {/* Fallback: if no text or questionText, show prompt */}
        {!text && !questionText && prompt && (
          <div className="mb-0">
            <h2 className="m-0 text-xl font-medium text-[var(--text-primary)] leading-relaxed">{prompt}</h2>
          </div>
        )}

        <div className="relative mt-0 flex flex-col gap-2" style={{ pointerEvents: 'auto' }}>
          {/* Audio Player - Your Recording */}
          {audioUrl ? (
            <div className="relative z-10 bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(14,165,233,0.07)_100%)] dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(37,99,235,0.06)_100%)] border-2 border-blue-400/30 dark:border-blue-400/35 rounded-2xl p-5" style={{ pointerEvents: 'auto' }}>
              <h3 className="m-0 mb-4 text-base font-semibold text-[var(--text-primary)]">Your Recording</h3>
              <QuestionAudioPlayer
                audioUrl={audioUrl}
                showQuota={false}
                maxPlaysMode="unlimited"
                fullWidth={true}
              />
            </div>
          ) : (
            <div className="bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(14,165,233,0.07)_100%)] dark:bg-[linear-gradient(135deg,rgba(59,130,246,0.12)_0%,rgba(37,99,235,0.06)_100%)] border-2 border-blue-400/30 dark:border-blue-400/35 rounded-2xl p-5">
              <h3 className="m-0 mb-4 text-base font-semibold text-[var(--text-primary)]">Your Recording</h3>
              <div className="py-2.5 text-center text-sm text-[var(--text-tertiary)]">
                No audio recording available
              </div>
            </div>
          )}
          
          {/* Feedback Section */}
          {displayFeedback && (
            <div className="mt-5 p-5 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-500/10">
              <h4 className="text-sm font-bold uppercase tracking-wide text-orange-600 dark:text-amber-400 mb-2">
                💡 Pembahasan
              </h4>
              <div className="text-[15px] leading-[1.7] text-gray-800 dark:text-slate-200 [&_p]:m-0">
                {displayFeedback}
              </div>
            </div>
          )}

          {/* Explanation */}
          {explanation && (
            <div className={`bg-[linear-gradient(135deg,#fff9e6_0%,#ffeaa7_100%)] dark:bg-[linear-gradient(135deg,#3d3520_0%,#4a3d1a_100%)] p-5 rounded-xl mb-6 border-l-4 border-l-[#ffd93d] ${displayFeedback ? 'mt-1' : ''}`}>
              <h3 className="text-base font-bold text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">Penjelasan</h3>
              <p className="text-[15px] text-[var(--text-primary)] leading-relaxed m-0">{explanation}</p>
            </div>
          )}

          <RecapSection recap={recap} annotations={annotations} question={question} />
        </div>
      </div>
    </div>
  );
};

export default SpeakingReview;

